import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@tanstack/react-query";
import ReturnModalProps from "@/app/interfaces/props/ReturnModalProps";
import QuickReturnPayload from "@/app/interfaces/payload/QuickReturnPaylaod";
import dataService from "@/app/services/dataService";
import PageLoadingOverlay from "../shared/pageLoadingOverlay";
import { ReturnModalEnum } from "@/enums/ReturnModalEnum";
import ThankYouDialog from "../order/ReturnOrder/ThankYouDialog";

const ReturnModal = ({ open, orderId, onClose }: ReturnModalProps) => {
  const [mode, setMode] = useState<ReturnModalEnum>(ReturnModalEnum.OPTIONS);
  const [note, setNote] = useState<string>("");
  const [onPallet, setOnPallet] = useState<boolean>(false);
  const [showThankYou, setShowThankYou] = useState<boolean>(false);

  const isNoteInvalid = !note.trim();

  const handleResetState = () => {
    setMode(ReturnModalEnum.OPTIONS);
    setNote("");
    setOnPallet(false);
  };

  const handleClose = () => {
    handleResetState();
    onClose();
  };

  const handleQuickReturnClick = () => {
    setMode(ReturnModalEnum.QUICK);
  };

  const quickReturnMutation = useMutation({
    mutationFn: (payload: QuickReturnPayload) =>
      dataService.sendQuickReturnRequest(payload),
    onSuccess: () => {
      handleClose();
      setShowThankYou(true);
    },
  });

  const handleSubmitQuickReturn = () => {
    if (isNoteInvalid) return;

    quickReturnMutation.mutate({
      orderId,
      note: note.trim(),
      palletFlag: onPallet,
    });
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            textAlign: "center",
            fontWeight: 600,
            fontSize: "21px",
            px: 5,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {mode === ReturnModalEnum.OPTIONS ? "Return Items" : "Quick Return"}
          </Typography>
          {mode === ReturnModalEnum.OPTIONS && (
            <IconButton onClick={handleClose} size="large">
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>

        <DialogContent
          sx={{
            textAlign: "left",
            minHeight: 250,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {mode === ReturnModalEnum.OPTIONS && (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: "#555" }}>
                On the next page, please select the items from your order you
                would like to return.
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  className="blueButtonSolid"
                  href={`/lbpcustomer/order/ReturnItems?orderId=${orderId}`}
                >
                  SELECT PRODUCTS TO RETURN
                </Button>
              </Box>

              <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #eee" }}>
                <Typography variant="body2" sx={{ mb: 2, color: "#555" }}>
                  If you would like assistance with your return, please select
                  Quick Return and a member from your branch will reach out to
                  you shortly.
                </Typography>
              </Box>
            </>
          )}

          {mode === ReturnModalEnum.QUICK && (
            <>
              <Box sx={{ mb: 2 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => setMode(ReturnModalEnum.OPTIONS)}
                  sx={{
                    fontSize: "14px",
                    color: "#54afe2",
                    textDecoration: "none",
                  }}
                >
                  {"< Back to Return Items"}
                </Link>
              </Box>
              <TextField
                fullWidth
                multiline
                required
                minRows={4}
                placeholder="Please enter any special instructions you would like to share with the branch."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                error={isNoteInvalid && quickReturnMutation.isError}
                helperText={
                  isNoteInvalid && quickReturnMutation.isError
                    ? "Note is required."
                    : ""
                }
              />

              {isNoteInvalid && (
                <Typography variant="caption" color="error" fontWeight="bold">
                  Note is required to submit this request.
                </Typography>
              )}
              <Box sx={{ mt: 3, display: "flex", alignItems: "center" }}>
                <Typography
                  sx={{
                    fontFamily: '"Montserrat", Verdana, sans-serif',
                    fontSize: "14px",
                    color: "#494440",
                    mr: 2,
                  }}
                >
                  Was your order on a pallet?
                </Typography>
                <RadioGroup
                  row
                  value={onPallet ? "yes" : "no"}
                  onChange={(e) => setOnPallet(e.target.value === "yes")}
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                    },
                    "& .MuiRadio-root": {
                      transform: "scale(0.8)",
                    },
                  }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{ justifyContent: "flex-end", pr: 3, pb: 3, gap: 2 }}
        >
          {mode === ReturnModalEnum.QUICK ? (
            <Button
              type="submit"
              className="blueButtonOutline"
              onClick={handleSubmitQuickReturn}
              disabled={quickReturnMutation.isPending || isNoteInvalid}
            >
              SUBMIT REQUEST
            </Button>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                className="blueButtonOutline"
                onClick={handleQuickReturnClick}
              >
                QUICK RETURN
              </Button>
            </Box>
          )}
        </DialogActions>

        {quickReturnMutation.isPending && <PageLoadingOverlay />}
      </Dialog>
      <ThankYouDialog open={showThankYou} onClose={handleThankYouClose} />
    </>
  );
};

export default ReturnModal;
